// src/components/Admin/ActivityLogs.jsx

import API_BASE_URL from "../../services/config";

import React, { useState, useEffect } from "react";
import {
  Activity,
  Clock,
  User,
  BookOpen,
  CheckCircle,
  AlertCircle,
  Edit,
  Trash2,
  Search,
  Download,
  TrendingUp,
  Eye,
  EyeOff,
  RefreshCw
} from "lucide-react";
import { authFetch } from "../../auth";

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [showDetails, setShowDetails] = useState({});

  const actionTypes = [
    { value: "all", label: "All Actions", icon: <Activity className="h-4 w-4" /> },
    { value: "question_answered", label: "Answered Questions", icon: <CheckCircle className="h-4 w-4" /> },
    { value: "question_created", label: "New Questions", icon: <BookOpen className="h-4 w-4" /> },
    { value: "question_edited", label: "Edited Questions", icon: <Edit className="h-4 w-4" /> },
    { value: "question_deleted", label: "Deleted Questions", icon: <Trash2 className="h-4 w-4" /> },
    { value: "user_activity", label: "User Activity", icon: <User className="h-4 w-4" /> },
    { value: "system", label: "System Events", icon: <AlertCircle className="h-4 w-4" /> }
  ];

  // derive unique users from logs for filter
  const uniqueUsers = Array.from(
    new Set(logs.map((l) => l.user).filter(Boolean))
  );

  useEffect(() => {
    fetchActivityLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, searchTerm, actionFilter, userFilter, dateFilter]);

  const fetchActivityLogs = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await authFetch(
       `${API_BASE_URL}/api/admin/activity-logs`,
        { method: "GET" }
      );

      if (!res.ok) {
        throw new Error(`Failed to load activity logs (${res.status})`);
      }

      const data = await res.json();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data.content)
        ? data.content
        : [];

      setLogs(list);
    } catch (err) {
      console.error("Error fetching activity logs:", err);
      setError(err.message || "Failed to load activity logs");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((log) => {
        const desc = (log.description || "").toLowerCase();
        const user = (log.user || "").toLowerCase();
        const action = (log.action || "").toLowerCase();
        return (
          desc.includes(term) || user.includes(term) || action.includes(term)
        );
      });
    }

    // Action filter
    if (actionFilter !== "all") {
      filtered = filtered.filter((log) => log.action === actionFilter);
    }

    // User filter
    if (userFilter !== "all") {
      filtered = filtered.filter((log) => log.user === userFilter);
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const cutoff = new Date();

      switch (dateFilter) {
        case "today":
          cutoff.setHours(0, 0, 0, 0);
          break;
        case "yesterday":
          cutoff.setDate(now.getDate() - 1);
          cutoff.setHours(0, 0, 0, 0);
          break;
        case "week":
          cutoff.setDate(now.getDate() - 7);
          break;
        case "month":
          cutoff.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }

      filtered = filtered.filter((log) => {
        if (!log.timestamp) return false;
        const ts = new Date(log.timestamp);
        return ts >= cutoff;
      });
    }

    setFilteredLogs(filtered);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "question_answered":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "question_created":
        return <BookOpen className="h-4 w-4 text-blue-500" />;
      case "question_edited":
        return <Edit className="h-4 w-4 text-yellow-500" />;
      case "question_deleted":
        return <Trash2 className="h-4 w-4 text-red-500" />;
      case "user_activity":
        return <User className="h-4 w-4 text-purple-500" />;
      case "system":
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActionColor = (action) => {
    const colors = {
      question_answered: "bg-green-100 text-green-800 border-green-200",
      question_created: "bg-blue-100 text-blue-800 border-blue-200",
      question_edited: "bg-yellow-100 text-yellow-800 border-yellow-200",
      question_deleted: "bg-red-100 text-red-800 border-red-200",
      user_activity: "bg-purple-100 text-purple-800 border-purple-200",
      system: "bg-orange-100 text-orange-800 border-orange-200"
    };
    return colors[action] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getActionStats = () => {
    const stats = {};
    filteredLogs.forEach((log) => {
      if (!log.action) return;
      stats[log.action] = (stats[log.action] || 0) + 1;
    });
    return stats;
  };

  const exportLogs = () => {
    if (filteredLogs.length === 0) return;

    const esc = (v) => `"${String(v || "").replace(/"/g, '""')}"`;

    const headers = [
      "Timestamp",
      "Action",
      "User",
      "Description",
      "IP Address",
      "User Agent"
    ];

    const rows = filteredLogs.map((log) => {
      const timestamp = log.timestamp
        ? new Date(log.timestamp).toISOString()
        : "N/A";
      return [
        esc(timestamp),
        esc(log.action),
        esc(log.user),
        esc(log.description),
        esc(log.ip || "N/A"),
        esc(log.userAgent || "N/A")
      ];
    });

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join(
      "\n"
    );

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      "activity-logs-" + new Date().toISOString().split("T")[0] + ".csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleLogDetails = (logId) => {
    setShowDetails((prev) => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const actionStats = getActionStats();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent"></div>
        <p className="text-purple-600 font-medium">
          Loading activity logs...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <Activity className="h-8 w-8" />
              <h1 className="text-2xl font-bold">Activity Logs</h1>
            </div>
            <p className="text-purple-100">
              Track all system activities and user interactions
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{logs.length}</div>
            <p className="text-purple-100 text-sm">Total Activities</p>
          </div>
        </div>
        {error && (
          <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(actionStats)
          .slice(0, 4)
          .map(([action, count]) => {
            const actionConfig =
              actionTypes.find((a) => a.value === action) || {
                label: action
              };
            return (
              <div
                key={action}
                className="bg-white rounded-xl border border-gray-200 p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      {actionConfig.label}
                    </p>
                    <p className="text-2xl font-bold mt-1">{count}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${getActionColor(action)}`}>
                    {getActionIcon(action)}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Filters and Controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          {/* Action Filter */}
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            {actionTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          {/* User Filter */}
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="all">All Users</option>
            {uniqueUsers.map((user) => (
              <option key={user} value={user}>
                {user}
              </option>
            ))}
          </select>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>

          {/* Export Button */}
          <button
            onClick={exportLogs}
            className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg hover:opacity-90"
          >
            <Download className="h-5 w-5" />
            <span>Export Logs</span>
          </button>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">
            Recent Activities
          </h2>
          <button
            onClick={fetchActivityLogs}
            className="flex items-center space-x-2 text-purple-600 hover:text-purple-800"
          >
            <RefreshCw className="h-5 w-5" />
            <span>Refresh</span>
          </button>
        </div>

        {filteredLogs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Activity className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No activities found
            </h3>
            <p className="text-gray-600">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLogs.map((log) => (
              <div
                key={log.id}
                className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start space-x-4">
                  {/* Icon */}
                  <div className={`p-3 rounded-lg ${getActionColor(log.action)}`}>
                    {getActionIcon(log.action)}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getActionColor(
                            log.action
                          )}`}
                        >
                          {log.action
                            ? log.action.replace("_", " ").toUpperCase()
                            : "UNKNOWN"}
                        </span>
                        <span className="text-sm text-gray-500">
                          <Clock className="inline h-3 w-3 mr-1" />
                          {log.timestamp
                            ? new Date(log.timestamp).toLocaleString()
                            : "Unknown time"}
                        </span>
                      </div>
                      <button
                        onClick={() => toggleLogDetails(log.id)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {showDetails[log.id] ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>

                    <p className="text-gray-900 font-medium mb-1">
                      {log.description || "No description"}
                    </p>

                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center">
                          <User className="h-4 w-4 mr-1" />
                          {log.user || "Unknown user"}
                        </span>
                        {log.ip && (
                          <span className="font-mono">IP: {log.ip}</span>
                        )}
                      </div>

                      {log.id && (
                        <span className="text-xs">
                          ID: {String(log.id).slice(0, 8)}...
                        </span>
                      )}
                    </div>

                    {/* Details Panel */}
                    {showDetails[log.id] && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-bold text-gray-700 mb-2">
                              Details
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="font-medium">
                                  Timestamp:
                                </span>{" "}
                                {log.timestamp
                                  ? new Date(
                                      log.timestamp
                                    ).toISOString()
                                  : "Unknown"}
                              </div>
                              {log.metadata && (
                                <div>
                                  <span className="font-medium">
                                    Metadata:
                                  </span>
                                  <pre className="mt-1 p-2 bg-white rounded border text-xs overflow-x-auto">
                                    {JSON.stringify(
                                      log.metadata,
                                      null,
                                      2
                                    )}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <h4 className="font-bold text-gray-700 mb-2">
                              Technical Info
                            </h4>
                            <div className="space-y-2 text-sm">
                              {log.userAgent && (
                                <div>
                                  <span className="font-medium">
                                    User Agent:
                                  </span>
                                  <div className="mt-1 p-2 bg-white rounded border text-xs">
                                    {log.userAgent}
                                  </div>
                                </div>
                              )}
                              {log.sessionId && (
                                <div>
                                  <span className="font-medium">
                                    Session:
                                  </span>{" "}
                                  {log.sessionId}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary Footer */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">
              Activity Summary
            </h3>
            <p className="text-sm text-gray-600">
              Showing {filteredLogs.length} of {logs.length} activities •
              Last updated: {new Date().toLocaleTimeString()}
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              {Object.entries(actionStats).map(([action, count]) => (
                <div
                  key={action}
                  className="flex items-center space-x-1"
                >
                  <div
                    className={`w-3 h-3 rounded-full ${
                      getActionColor(action).split(" ")[0]
                    }`}
                  ></div>
                  <span className="text-xs text-gray-600">
                    {count}
                  </span>
                </div>
              ))}
            </div>

            <div className="text-sm text-gray-600">
              <TrendingUp className="inline h-4 w-4 mr-1 text-green-500" />
              {
                filteredLogs.filter(
                  (l) =>
                    l.timestamp &&
                    new Date(l.timestamp) >
                      new Date(Date.now() - 24 * 60 * 60 * 1000)
                ).length
              }{" "}
              today
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs;
